// pages/sections/BillingReviewSection.js
// Reporting view for the Client Billing Data Review section.
// Fetches merged PE + blob data from /api/diBillingReview/{period}.
// Displays a filterable, expandable client table with billing group,
// client/partner/manager standards, tech fees, and recurring billing data.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSectionData } from '../../hooks/useSectionData';
import { useAuth } from '../../auth/AuthContext';
import { usePeriod } from '../../hooks/usePeriod';
import ClientStandardsEditor from './ClientStandardsEditor';
import BillingGroupEditor from './BillingGroupEditor';
import RecurringBillingEditor, { isApplicableThroughExpired } from './RecurringBillingEditor';
import ForceBillEditor from './ForceBillEditor';
import NarrativeStandardsSummary from './narratives/NarrativeStandardsSummary';
import NarrativeStandardsModal from './narratives/NarrativeStandardsModal';
import ConfigToggle from './ConfigToggle';
import DeliveryEmailModal from './DeliveryEmailModal';
import Modal from '../../components/Modal';
import ManagerUnsignedWarningModal from '../../components/ManagerUnsignedWarningModal';
import PendingTag from './PendingTag';
import {
  submitChange, withdrawChange, pendingFor, pendingForKey, revertsPending,
  withPending, withoutPending, withdrawnView, wasWithdrawn, mutationsInFlight, mutationStamp, trackMutation,
} from './billingChangeApi';
import { BillingApprovals } from '../BillingApprovalsPage';
import LastRefreshed from '../../components/LastRefreshed';
import RoleToggle from './RoleToggle';
import ToggleSwitch from './ToggleSwitch';
import ClientBillingReportModal from './ClientBillingReportModal';
import { postJson, sortByTier } from './reviewUtil';
import './ReviewTable.css';
import './BillingReviewSection.css';

// ── helpers ──────────────────────────────────────────────────────────────────

function fmt$(n) {
  if (n == null) return '—';
  return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 0 });
}

function pct(n) {
  if (n == null) return '—';
  return n + '%';
}

// Boolean "only show clients with…" filters (Filters popover). Mirrors the
// Client Billing Configuration popover; entries for config that DI doesn't surface
// yet are added together with their sections. A row passes iff every active
// predicate passes.
const BOOL_FILTERS = [
  { key: 'pending',        label: 'Pending Billing Team approval', test: c => (c.pending || []).length > 0 },
  { key: 'standards',      label: 'Has client standards',  test: c => Array.isArray(c.clientStandards) && c.clientStandards.length > 0 },
  { key: 'forceBill',      label: 'Force-billed',          test: c => Array.isArray(c.forceBill) && c.forceBill.length > 0 },
  { key: 'recurMonthly',   label: 'Recurring monthly',     test: c => !!c.recurringMonthly },
  { key: 'recurQuarterly', label: 'Recurring quarterly',   test: c => !!c.recurringQuarterly },
  { key: 'techFee',        label: 'Tech fee broken out',   test: c => !!c.techFee },
  { key: 'appendName',     label: 'Append client name',    test: c => !!c.billingGroup?.appendClientName },
  { key: 'excluded',       label: 'Excluded from automated billing', test: c => !!c.excludeClient },
  { key: 'freebie',        label: '"Freebie" return',      test: c => !!c.freebieReturn },
  { key: 'rollIndtax',     label: 'Roll INDTAX into BUSTAX', test: c => !!c.rollIndtaxIntoBustax },
  { key: 'lumpDisb',       label: 'Lump disbursements (recurring)', test: c => !!c.recurringMonthly?.lumpDisbursements || !!c.recurringQuarterly?.lumpDisbursements },
  { key: 'lumpWithParent', label: 'Lump BG children WIP (recurring)', test: c => !!c.recurringMonthly?.lumpWithParent || !!c.recurringQuarterly?.lumpWithParent },
  { key: 'expiredRecur',   label: 'Expired recurring bill', test: c => isApplicableThroughExpired(c.recurringMonthly?.applicableThrough) || isApplicableThroughExpired(c.recurringQuarterly?.applicableThrough) },
];
const EMPTY_BOOL_FILTERS = Object.fromEntries(BOOL_FILTERS.map(f => [f.key, false]));

const uniqSorted = arr => [...new Set(arr.filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b)));

// Billing Group parent picker: any firm client the server offers (not only the viewer's book).
async function searchParentCandidates(period, needle, forClient) {
  const qs = new URLSearchParams({ parentSearch: needle, forClient: forClient || '' });
  const res = await fetch(`/api/diBillingReview/${encodeURIComponent(period)}?${qs}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()).parents || [];
}

// Members of the billing group headed by `code` (code + name), or null when it isn't a client.
async function fetchGroupMembers(period, code, forClient) {
  const qs = new URLSearchParams({ parentMembers: code, forClient: forClient || '' });
  const res = await fetch(`/api/diBillingReview/${encodeURIComponent(period)}?${qs}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()).group || null;
}

// Run `worker` over `items` with at most `limit` in flight.
async function runLimited(items, limit, worker) {
  const queue = [...items];
  await Promise.all(Array.from({ length: Math.min(limit, queue.length) }, async () => {
    while (queue.length) await worker(queue.shift());
  }));
}

// ── component ────────────────────────────────────────────────────────────────

// The Billing Team lands on the approvals queue. Admins get a third "Approvals" option on the
// Manager / Partner toggle (choice is remembered for the tab). A Billing Team member who also
// manages / partners clients gets the same three-way toggle (defaulting to Approvals); one with no
// clients keeps the plain queue. Everyone else gets the review page.
const VIEW_KEY = 'di.billingReview.view';

export default function BillingReviewSection() {
  const { isAdmin, billingTeamMember, impersonating, email } = useAuth();
  const { period } = usePeriod();
  // A real admin in their own session gets the switch; while "Viewing As" someone, show exactly what
  // that person sees (an approver lands on the queue).
  const adminControls = isAdmin && !impersonating;
  const billingOnly = billingTeamMember && !adminControls;
  // null = not chosen yet (the review page then picks the role the person actually has).
  const [view, setView] = useState(() => {
    try {
      const v = sessionStorage.getItem(VIEW_KEY);
      return v === 'manager' || v === 'partner' || v === 'approvals' ? v : null;
    } catch { return null; }
  });
  const chooseView = v => {
    setView(v);
    try { sessionStorage.setItem(VIEW_KEY, v); } catch { /* private mode */ }
  };

  // Billing Team member: do they manage / partner any clients? A small probe (no client data) —
  // the full list is only fetched once a client view is actually chosen. null = unknown yet.
  const [owns, setOwns] = useState(null);
  useEffect(() => {
    if (!billingOnly || !period) return undefined;
    let live = true;
    setOwns(null);
    fetch(`/api/diBillingReview/${encodeURIComponent(period)}?ownership=1`, { credentials: 'include' })
      .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
      .then(body => { if (live) setOwns({ manager: !!body.owns?.manager, partner: !!body.owns?.partner }); })
      .catch(() => { if (live) setOwns({ manager: false, partner: false }); });   // fall back to the plain queue
    return () => { live = false; };
  }, [billingOnly, period, email]);

  if (billingOnly) {
    const ownsClients = !!owns && (owns.manager || owns.partner);
    if (!ownsClients) return <BillingApprovals />;
    // A remembered Manager / Partner choice only counts if they own clients in that role.
    const chosen = (view === 'manager' || view === 'partner') && !owns[view] ? 'approvals' : (view || 'approvals');
    if (chosen === 'approvals') {
      return <BillingApprovals actions={<RoleToggle role="approvals" onChange={chooseView} withApprovals />} />;
    }
    return <BillingReviewMain selectedRole={chosen} onRoleChange={chooseView} withApprovals />;
  }
  if (adminControls && view === 'approvals') {
    return <BillingApprovals actions={<RoleToggle role="approvals" onChange={chooseView} withApprovals />} />;
  }
  return <BillingReviewMain selectedRole={view === 'partner' || view === 'manager' ? view : null} onRoleChange={chooseView} withApprovals={adminControls} />;
}

function BillingReviewMain({ selectedRole, onRoleChange, withApprovals }) {
  const { isAdmin, email } = useAuth();
  const { period, periodActive } = usePeriod();

  // filters
  const [search, setSearch]             = useState('');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [managerFilter, setManagerFilter] = useState('');
  const [officeFilter, setOfficeFilter]   = useState('');
  const [boolFilters, setBoolFilters]     = useState(EMPTY_BOOL_FILTERS);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  // Partner-only quick filters (shown beside the Filters button, not inside it).
  const [ptrMgrSigned, setPtrMgrSigned]     = useState(false);   // only rows the manager has signed off
  const [ptrChangedOnly, setPtrChangedOnly] = useState(false);   // only clients with change requests
  const filterPanelRef = useRef(null);

  // Client Billing Report modal: reportCodes = null → everything in my book,
  // an array → just those client(s) (opened from a row's drill-down).
  const [reportOpen, setReportOpen]   = useState(false);
  const [reportCodes, setReportCodes] = useState(null);
  const [reportTitle, setReportTitle] = useState('');
  const navigate = useNavigate();

  // Narrative Standards modal: which client it's open for + a counter that makes each open
  // drill-down's summary re-read after a submit.
  const [narrContIndex, setNarrContIndex] = useState(null);
  const [narrReload, setNarrReload] = useState(0);

  // Filters popover: dismiss on Esc / outside click.
  useEffect(() => {
    if (!showFilterPanel) return undefined;
    const onKey = e => { if (e.key === 'Escape') setShowFilterPanel(false); };
    const onDown = e => {
      if (filterPanelRef.current && !filterPanelRef.current.contains(e.target)) setShowFilterPanel(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [showFilterPanel]);

  // pagination
  const [page, setPage]       = useState(1);
  const PAGE_SIZE             = 25;

  // expanded detail rows
  const [expanded, setExpanded] = useState(new Set());

  // Track which rows are being saved
  const [savingReview, setSavingReview] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);       // Bulk Approval in flight
  const [bulkMode, setBulkMode] = useState(false);       // Bulk Approval: pick clients with checkboxes
  const [selected, setSelected] = useState(new Set());   // contIndexes ticked in bulk mode
  const [mgrWarn, setMgrWarn] = useState(null);          // { unsigned, total } — partner bulk-signing clients the manager hasn't
  const [savingKeys, setSavingKeys] = useState(new Set());   // `${contIndex}:${fieldKey}` of in-flight proposals
  const [emailModal, setEmailModal] = useState(null);        // { contIndex, field } — Email delivery awaiting its three addresses
  const [excludeModal, setExcludeModal] = useState(null);    // { contIndex, fieldKey } — Freebie / Discount turned on while the other is on

  // Fire data-quality toasts once per mount; silent polling refreshes
  // shouldn't spam the user.
  const firstLoadToastsFired = useRef(false);

  const fetcher = useCallback(async () => {
    if (!period) return null;
    const stampAtStart = mutationStamp();
    const res = await fetch(`/api/diBillingReview/${period}`, { credentials: 'include' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const body = await res.json();
    body.mutationStamp = stampAtStart;

    if (!firstLoadToastsFired.current) {
      firstLoadToastsFired.current = true;
      if (body.summary?.billingGroupViolations > 0) {
        toast.warning(`⚠️ ${body.summary.billingGroupViolations} client(s) with billing group violations detected.`, { autoClose: 6000 });
      }
      if (body.summary?.recurringDuplicates > 0) {
        toast.warning(`⚠️ ${body.summary.recurringDuplicates} client(s) with duplicate recurring frequencies.`, { autoClose: 6000 });
      }
    }
    return body;
  }, [period]);

  const {
    data, setData, initialLoading, error,
    lastRefreshedAt, freshness, silentRefetch,
  } = useSectionData(fetcher, {
    deps: [period, email],
    heartbeat: { sections: ['client-billing-review'], period },
    // Keep optimistic edits on screen while submits / withdraws are still on the wire: a refetch that
    // started before the latest one finished doesn't know about it, so it must not overwrite the grid.
    reconcile: (prev, next) => (mutationsInFlight() > 0 || next.mutationStamp !== mutationStamp() ? prev : next),
  });

  // ── loading / error states ───────────────────────────────────────────────
  // Guard on initialLoading (first load only) so background polls / refetches
  // update in place instead of blanking the page.

  const backButton = (
    <button type="button" className="back-btn br-back-btn" onClick={() => navigate('/')} title="Back to the dashboard">
      <i className="fas fa-arrow-left" /> Dashboard
    </button>
  );
  if (initialLoading) return (
    <div className="billing-review">{backButton}<div className="br-loading">Loading billing review data...</div></div>
  );
  if (error && !data) return (
    <div className="billing-review">{backButton}<div className="br-error">Error: {error}</div></div>
  );
  if (!data) return null;

  const { clients: allClients } = data;
  // Client-side role filter — a user sees a client in manager view only when
  // they're the assigned ClientManager, and in partner view only when they're
  // the assigned ClientPartner. Applies to everyone including admins; firm-
  // wide visibility lives in Steering.
  const myEmailLc = (email || '').toLowerCase();
  const isMineAs = (c, role) => ((role === 'manager' ? c.manager?.email : c.partner?.email) || '').toLowerCase() === myEmailLc;
  // The user can view as manager or partner; switching filters client-side (no refetch). Until they
  // pick one, default to a role they actually have: partner only when they're ONLY a partner.
  const hasManagerClients = allClients.some(c => isMineAs(c, 'manager'));
  const hasPartnerClients = allClients.some(c => isMineAs(c, 'partner'));
  const activeRole = selectedRole || (hasPartnerClients && !hasManagerClients ? 'partner' : 'manager');
  const clients = allClients.filter(c => isMineAs(c, activeRole));

  // ── filters ──────────────────────────────────────────────────────────────
  // Each dropdown only offers values that exist among the rows that pass every
  // OTHER active filter (faceted): filter to one client and Partner/Manager/
  // Office collapse to that client's values. A selected value is always kept in
  // its own list so the select never shows a blank.
  const searchLc = search.trim().toLowerCase();
  const activeBool = BOOL_FILTERS.filter(f => boolFilters[f.key]);
  const passes = (c, skip) =>
    (!searchLc || c.clientCode.toLowerCase().includes(searchLc) || c.clientName.toLowerCase().includes(searchLc)) &&
    (skip === 'partner' || !partnerFilter || c.partner.name === partnerFilter) &&
    (skip === 'manager' || !managerFilter || c.manager.name === managerFilter) &&
    (skip === 'office'  || !officeFilter  || c.clientOffice === officeFilter) &&
    activeBool.every(f => f.key === skip || f.test(c)) &&
    (activeRole !== 'partner' || ((!ptrMgrSigned || !!c.reviews?.manager) && (!ptrChangedOnly || !!c.hasChanges)));

  const withSelected = (vals, sel) => (sel && !vals.includes(sel) ? uniqSorted([...vals, sel]) : vals);
  const partners = withSelected(uniqSorted(clients.filter(c => passes(c, 'partner')).map(c => c.partner.name)), partnerFilter);
  const managers = withSelected(uniqSorted(clients.filter(c => passes(c, 'manager')).map(c => c.manager.name)), managerFilter);
  const offices  = withSelected(uniqSorted(clients.filter(c => passes(c, 'office')).map(c => c.clientOffice)), officeFilter);
  // How many rows each toggle would leave (with every other filter applied).
  const boolCounts = Object.fromEntries(BOOL_FILTERS.map(f =>
    [f.key, clients.filter(c => passes(c, f.key) && f.test(c)).length]));

  const filtered = clients.filter(c => passes(c, null));

  const activeChips = [
    partnerFilter && { key: 'partner', label: `Partner: ${partnerFilter}`, clear: () => setPartnerFilter('') },
    managerFilter && { key: 'manager', label: `Manager: ${managerFilter}`, clear: () => setManagerFilter('') },
    officeFilter  && { key: 'office',  label: `Office: ${officeFilter}`,   clear: () => setOfficeFilter('') },
    ...activeBool.map(f => ({ key: f.key, label: f.label, clear: () => setBoolFilters(prev => ({ ...prev, [f.key]: false })) })),
  ].filter(Boolean);
  const partnerQuick = activeRole === 'partner' && (ptrMgrSigned || ptrChangedOnly);
  const anyFilter = !!(search || activeChips.length || partnerQuick);

  function clearFilters() {
    setSearch(''); setPartnerFilter(''); setManagerFilter(''); setOfficeFilter('');
    setBoolFilters(EMPTY_BOOL_FILTERS); setPtrMgrSigned(false); setPtrChangedOnly(false); setPage(1);
  }

  // The manager and partner lists differ, so a role switch starts from page 1 with no filters / search
  // (a stale Partner or Office pick would otherwise silently hide rows in the other role's list).
  function changeRole(role) {
    clearFilters();
    setShowFilterPanel(false);
    exitBulk();                                   // a selection made as one role must not sign as the other
    onRoleChange(role);
  }

  // ── default sort by role-specific review state ───────────────────────────
  // Manager view: unreviewed-by-me at top, reviewed-by-me at bottom.
  // Manager view: open → reviewed. Partner view: (1) reviewed by the manager, waiting on me → top,
  // (2) not yet reviewed by the manager → middle, (3) signed off by me → bottom. Each tier by ClientCode.
  const sorted = sortByTier(filtered, c => {
    const mgrReviewed = !!c.reviews?.manager;
    const ptrReviewed = !!c.reviews?.partner;
    if (activeRole === 'manager') return mgrReviewed ? 1 : 0;
    if (ptrReviewed) return 2;
    return mgrReviewed ? 0 : 1;
  });

  // ── pagination ─────────────────────────────────────────────────────────
  const totalFiltered = sorted.length;
  const totalPages    = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));
  const safePage      = Math.min(page, totalPages);
  const paged         = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // ── expand toggle ────────────────────────────────────────────────────────

  function toggle(contIndex) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(contIndex)) next.delete(contIndex); else next.add(contIndex);
      return next;
    });
  }

  // ── cell renderers ───────────────────────────────────────────────────────

  function renderBgCell(client) {
    const bg = client.billingGroup;
    // No billing-group entry in the blob → treat as standalone (self).
    if (!bg) return <td className="cell-self">Self</td>;

    if (bg.violation) {
      return (
        <td className="cell-violation">
          <i className="fas fa-exclamation-triangle" />
          <span className="violation-label">Violation</span>
        </td>
      );
    }
    if (bg.isParent) {
      const n = typeof bg.childCount === 'number'
        ? bg.childCount
        : (bg.children || []).filter(c => c !== client.clientCode).length;
      return (
        <td className="cell-parent">
          Parent <span className="child-badge">{n}</span>
        </td>
      );
    }
    if (!bg.isSelfOnly) {
      return (
        <td className="cell-child">
          <i className="fas fa-level-up-alt fa-rotate-90" /> {bg.parentCode}
        </td>
      );
    }
    return <td className="cell-self">Self</td>;
  }

  function renderStdCell(standards) {
    if (!standards || !standards.length) return <td className="cell-na">—</td>;
    const all = standards.find(s => s.service === 'ALL');
    const others = standards.filter(s => s.service !== 'ALL');
    return (
      <td>
        {all ? pct(all.value) : '—'}
        {others.length > 0 && <span className="svc-badge">+{others.length}</span>}
      </td>
    );
  }

  function renderRecurCell(recur, isDupe) {
    if (!recur) return <td className="cell-na">—</td>;
    return (
      <td className={isDupe ? 'cell-violation' : ''}>
        <span className="recur-amount">{fmt$(recur.billAmount)}</span>
        <span className={`recur-type-badge ${recur.billType.toLowerCase()}`}>{recur.billType}</span>
        {isDupe && <i className="fas fa-exclamation-triangle dupe-icon" title="Duplicate frequency" />}
        {isApplicableThroughExpired(recur.applicableThrough) && (
          <span className="rbe-expired-bubble" title="Past the Applicable Through date — this recurring bill will no longer be created.">Expired</span>
        )}
      </td>
    );
  }

  function renderTechFeeCell(client) {
    if (client.techFee) {
      return (
        <td className="cell-tech-fee cell-tech-fee--on" title="Tech fee: yes">
          <i className="fas fa-check-circle" />
        </td>
      );
    }
    return (
      <td className="cell-tech-fee cell-tech-fee--off" title="Tech fee: no">
        <i className="fas fa-times-circle" />
      </td>
    );
  }

  function renderAppendCell(client) {
    const on = !!client.billingGroup?.appendClientName;
    return (
      <td className={`cell-tech-fee ${on ? 'cell-tech-fee--on' : 'cell-tech-fee--off'}`}
          title={`Append client name on invoice: ${on ? 'yes' : 'no'}`}>
        <i className={`fas ${on ? 'fa-check-circle' : 'fa-times-circle'}`} />
      </td>
    );
  }

  // Apply an in-place update to a single client in data.clients. Used for
  // optimistic updates and for restoring snapshots on failure.
  function patchClient(contIndex, updater) {
    setData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        clients: prev.clients.map(c => c.contIndex === contIndex ? updater(c) : c),
      };
    });
  }

  const fireAdminUnmark = (contIndex, role) => trackMutation(() =>
    postJson(`/api/diAdminUnmark/${period}/client-billing-review`, { recordKey: String(contIndex), role }));

  async function unmarkClientReviewed(client) {
    if (!isAdmin) return;
    // Cascade-unmark when manager and partner are the same user — mirrors
    // the cascade-mark path so both signoffs stay in sync.
    const mgrEm = (client.manager?.email || '').toLowerCase();
    const ptrEm = (client.partner?.email || '').toLowerCase();
    const cascade = !!mgrEm && mgrEm === ptrEm;

    const confirmMsg = cascade
      ? `Unmark ${client.clientCode} as reviewed? Manager and Partner are the same user, so both signoffs will be cleared.`
      : `Unmark ${client.clientCode} as reviewed for ${activeRole}? They will need to review again.`;
    if (!window.confirm(confirmMsg)) return;

    const snapshotReviews = client.reviews;
    // Optimistic update.
    patchClient(client.contIndex, c => {
      if (cascade) return { ...c, reviews: {} };
      const { [activeRole]: _dropped, ...rest } = c.reviews || {};
      return { ...c, reviews: rest };
    });
    setSavingReview(prev => new Set(prev).add(client.contIndex));

    let managerUnmarked = false;
    try {
      if (cascade) {
        await fireAdminUnmark(client.contIndex, 'manager');
        managerUnmarked = true;
        await fireAdminUnmark(client.contIndex, 'partner');
        toast.success(`✓ Unmarked ${client.clientCode} (manager + partner).`);
      } else {
        await fireAdminUnmark(client.contIndex, activeRole);
        toast.success(`✓ Unmarked ${client.clientCode} (${activeRole}).`);
      }
    } catch (err) {
      // If the manager half was already cleared, only the partner sign-off is put back.
      patchClient(client.contIndex, c => ({
        ...c,
        reviews: managerUnmarked ? (({ manager: _m, ...rest }) => rest)(snapshotReviews || {}) : snapshotReviews,
      }));
      toast.error(`Failed to unmark: ${err.message}`, { autoClose: 10000 });
    } finally {
      setSavingReview(prev => { const n = new Set(prev); n.delete(client.contIndex); return n; });
    }
  }

  // The manager takes their submission back (an admin uses unmarkClientReviewed instead). The server only
  // allows it while the partner hasn't signed off or requested changes; one person holding both roles
  // loses both sign-offs.
  async function unsubmitClientReview(client) {
    if (!window.confirm(`Unsubmit ${client.clientCode}? You'll be able to edit it again, and will need to submit it again when you're done.`)) return;
    const both = holdsBothRoles(client);
    const snapshotReviews = client.reviews;
    patchClient(client.contIndex, c => {
      const { manager: _m, ...rest } = c.reviews || {};
      return { ...c, reviews: both ? {} : rest };
    });
    setSavingReview(prev => new Set(prev).add(client.contIndex));
    try {
      await trackMutation(() => postJson(`/api/diBillingUnsubmit/${encodeURIComponent(period)}`, { contIndex: client.contIndex }));
      toast.success(`✓ ${client.clientCode} unsubmitted — you can edit it again.`);
      silentRefetch();
    } catch (err) {
      patchClient(client.contIndex, c => ({ ...c, reviews: snapshotReviews }));
      toast.error(`Couldn't unsubmit: ${err.message}`, { autoClose: 10000 });
    } finally {
      setSavingReview(prev => { const n = new Set(prev); n.delete(client.contIndex); return n; });
    }
  }

  // Propose an on/off setting. Nothing is live until the Billing Team approves: the grid
  // shows the proposed value and its "Pending approval" tag at once (optimistic; the server's
  // overlay replaces it on refetch, a failure restores the row). Flipping a setting back to what's
  // live withdraws the open request instead. Locked once the active role has signed off.
  // Resolves true once the server took it, false if it was refused / not attempted.
  async function proposeToggle(client, { section, fieldKey, value, label, optimistic, snapshot }) {
    const key = `${client.contIndex}:${fieldKey}`;
    const reviewed = client.reviews?.[activeRole];
    if (savingKeys.has(key) || !periodActive || !activeRole || reviewed) return false;

    const body = { section, fieldKey, payload: { value } };
    const undo = revertsPending(pendingForKey(client, section, fieldKey, body.payload), body, email);
    const priorPending = client.pending || [];
    patchClient(client.contIndex, c => {
      const next = optimistic(c);
      return undo
        ? withoutPending(next, section, fieldKey, body.payload)
        : withPending(next, { ...body, summary: `${label}: ${value ? 'on' : 'off'}`, email });
    });
    setSavingKeys(prev => new Set(prev).add(key));
    try {
      const out = await submitChange({ period, contIndex: client.contIndex, ...body });
      toast.success(wasWithdrawn(out)
        ? `${label} change for ${client.clientCode} withdrawn.`
        : `${label} change for ${client.clientCode} submitted for Billing Team approval.`);
      silentRefetch();
      return true;
    } catch (err) {
      patchClient(client.contIndex, c => ({ ...snapshot(c), pending: priorPending }));
      toast.error(`Failed to update ${label.toLowerCase()}: ${err.message}`, { autoClose: 10000 });
      return false;
    } finally {
      setSavingKeys(prev => { const n = new Set(prev); n.delete(key); return n; });
    }
  }

  const setFlag =(client, fieldKey, label, value) => proposeToggle(client, {
    section: 'misc', fieldKey, value, label,
    optimistic: c => ({ ...c, [fieldKey]: value }),
    snapshot: c => ({ ...c, [fieldKey]: client[fieldKey] }),
  });

  // Freebie Return and Discount Toggle are mutually exclusive. Turning one ON while the other shows on (live or
  // pending) asks first (excludeModal), then files both: the other one off, then this one on. Anything else
  // (turning off, the other already off) goes straight through.
  const EXCLUSIVE = {
    freebieReturn: { label: 'Freebie return', other: 'discountToggle', otherLabel: 'Discount toggle' },
    discountToggle: { label: 'Discount toggle', other: 'freebieReturn', otherLabel: 'Freebie return' },
  };
  function requestExclusiveFlag(client, fieldKey, value) {
    const x = EXCLUSIVE[fieldKey];
    if (value && client[x.other]) { setExcludeModal({ contIndex: client.contIndex, fieldKey }); return; }
    setFlag(client, fieldKey, x.label, value);
  }
  async function applyExclusiveFlag(client, fieldKey) {
    const x = EXCLUSIVE[fieldKey];
    // The other one first: if that is refused, nothing else is filed.
    if (!(await setFlag(client, x.other, x.otherLabel, false))) return;
    if (!(await setFlag(client, fieldKey, x.label, true))) {
      toast.warning(`${x.otherLabel} was still submitted to turn off for ${client.clientCode}. Withdraw it from its Pending tag if you didn't want that.`, { autoClose: 10000 });
      silentRefetch();
    }
  }

  const setAppendClientName = (client, value) => proposeToggle(client, {
    section: 'billing_group', fieldKey: 'append_client_name', value, label: 'Append client name',
    optimistic: c => ({
      ...c,
      billingGroup: c.billingGroup
        ? { ...c.billingGroup, appendClientName: value }
        : { parentCode: c.clientCode, children: [c.clientCode], isParent: false, isSelfOnly: true, violation: false, appendClientName: value },
    }),
    snapshot: c => ({ ...c, billingGroup: client.billingGroup }),
  });

  // Picking a delivery format. Once BOTH invoice and statement delivery are Email the client needs the three
  // delivery addresses, asked for once — by the pick that completes the pair (the other format is already Email,
  // live or pending, as `client` shows it) — via DeliveryEmailModal, and only submitted from there. A pick that
  // merely puts the setting back to what's live (taking back your own open request) needs no addresses.
  function requestDeliveryFormat(client, field, value) {
    const otherField = field === 'ClientInvDeliveryFormat' ? 'ClientDeliveryFormat' : 'ClientInvDeliveryFormat';
    if (value === 1 && client[otherField] === 1) {
      const body = { section: 'delivery_format', fieldKey: field, payload: { value } };
      if (!revertsPending(pendingForKey(client, body.section, field, body.payload), body, email)) {
        setEmailModal({ contIndex: client.contIndex, field });
        return;
      }
    }
    proposeDeliveryFormat(client, field, value);
  }

  // `emails` ({ creditEmail, billingEmail, statementEmail }) travels with the Email request that completes an
  // invoice + statement Email pair (the server refuses that request without them).
  async function proposeDeliveryFormat(client, field, value, emails = null) {
    const key = `${client.contIndex}:${field}`;
    if (savingKeys.has(key) || !periodActive || !activeRole || client.reviews?.[activeRole]) return;
    const previous = client[field];
    const previousEmails = { ClientCreditEmail: client.ClientCreditEmail, ClientBillingEmail: client.ClientBillingEmail, ClientStatementEmail: client.ClientStatementEmail };
    const priorPending = client.pending || [];
    const body = { section: 'delivery_format', fieldKey: field, payload: value === 1 && emails ? { value, ...emails } : { value } };
    const undo = revertsPending(pendingForKey(client, body.section, field, body.payload), body, email);
    patchClient(client.contIndex, c => {
      const next = {
        ...c, [field]: value,
        ...(value === 1 && emails ? { ClientCreditEmail: emails.creditEmail, ClientBillingEmail: emails.billingEmail, ClientStatementEmail: emails.statementEmail } : {}),
      };
      return undo
        ? withoutPending(next, body.section, field, body.payload)
        : withPending(next, { ...body, summary: `${field === 'ClientInvDeliveryFormat' ? 'Invoice' : 'Statement'} delivery: ${value === 1 ? 'Email' : 'Print and Mail'}`, email });
    });
    setSavingKeys(prev => new Set(prev).add(key));
    try {
      const out = await submitChange({ period, contIndex: client.contIndex, ...body });
      toast.success(wasWithdrawn(out)
        ? `Delivery format change for ${client.clientCode} withdrawn.`
        : `Delivery format change for ${client.clientCode} submitted for Billing Team approval (written to PE when approved).`);
      silentRefetch();
    } catch (err) {
      patchClient(client.contIndex, c => ({ ...c, [field]: previous, ...previousEmails, pending: priorPending }));
      toast.error(`Failed to update delivery format: ${err.message}`, { autoClose: 10000 });
    } finally {
      setSavingKeys(prev => { const n = new Set(prev); n.delete(key); return n; });
    }
  }

  // Take back one of your own pending changes (the server also refuses anyone else's). Optimistic:
  // the tag disappears and the setting returns to its live value at once, restored if it fails.
  async function withdraw(change) {
    const prior = data.clients.find(c => c.contIndex === change.contIndex);
    const view = prior ? withdrawnView(prior, change) : null;
    if (view) patchClient(change.contIndex, () => view);
    try {
      await withdrawChange({ period, changeId: change.changeId });
      toast.success('Change withdrawn.');
      silentRefetch();
    } catch (err) {
      if (view) patchClient(change.contIndex, () => prior);
      toast.error(`Couldn't withdraw: ${err.message}`, { autoClose: 10000 });
    }
  }

  function renderReviewBadge(reviews, role) {
    const r = reviews?.[role];
    if (r) return <span className="review-done" title={`${r.reviewedBy} on ${new Date(r.reviewedAt).toLocaleDateString()}`}><i className="fas fa-check-circle" /></span>;
    return <span className="review-pending"><i className="fas fa-minus-circle" /></span>;
  }

  // Row-level review toggle for the active role. Slider goes off→on for Mark
  // Reviewed (with pull-to-self semantics when partner is acting on a row
  // whose manager hasn't reviewed); on→off is admin-only and unmarks. The
  // Pending Manager corner badge cues the pull-to-self path. Clicks are
  // stopped from bubbling so they don't also toggle the row's expand state.
  function renderReviewActionCell(client) {
    if (!periodActive || !activeRole) {
      return <td className="cell-review">{renderReviewBadge(client.reviews, activeRole)}</td>;
    }
    const reviewed = !!client.reviews?.[activeRole];
    const isSaving = savingReview.has(client.contIndex);
    const partnerSigned  = activeRole === 'manager' && !!client.reviews?.partner;
    // A manager can take their submission back until the partner has signed off or made changes.
    const canUnsubmit = activeRole === 'manager' && reviewed && !partnerSigned && (holdsBothRoles(client) || !client.partnerEdited);
    const lockedWhy = activeRole === 'manager' && reviewed && !canUnsubmit
      ? (partnerSigned ? 'The partner has signed off, so this can no longer be unsubmitted.' : 'The partner has made changes, so this can no longer be unsubmitted.')
      : (canUnsubmit ? 'Submitted — switch off to unsubmit and edit again (until the partner signs off or makes changes).' : undefined);

    return (
      <td className="rt-mark-reviewed-cell br-review-cell" onClick={e => e.stopPropagation()}>
        {/* Badge sits above the toggle in normal flow (not absolutely positioned),
            so it can't be clipped by / overlap the sign-off control. */}
        <div className="br-review-stack" title={lockedWhy}>
          {partnerSigned && (
            <span className="rt-partner-signed-corner"
                  title="Partner has already signed off on this client.">
              Partner Signed Off
            </span>
          )}
          <ToggleSwitch
            checked={reviewed}
            // Only an already-signed manager toggle is ever locked. A manager who hasn't signed yet can
            // always sign (even after the partner did) — otherwise that client is a dead end.
            disabled={!isAdmin && reviewed && (partnerSigned || !canUnsubmit)}
            onChange={checked => {
              // The switch flips at once (optimistic) with no "saving…" badge; a second click is ignored
              // until the first request lands, and a failure flips it back.
              if (isSaving) return;
              if (checked && !reviewed) markClientReviewed(client);
              else if (!checked && reviewed) {
                if (isAdmin) unmarkClientReviewed(client);
                else if (canUnsubmit) unsubmitClientReview(client);
              }
            }}
          />
          {partnerSigned && !reviewed && (
            <span className="br-review-hint">The partner already signed — sign off to complete this client.</span>
          )}
        </div>
      </td>
    );
  }

  // One person who is both the client's manager and partner: the server writes both sign-offs from a
  // single one (their manager sign-off counts as the partner's), so only one needs to be posted.
  const holdsBothRoles = client => {
    const me = (email || '').toLowerCase();
    return !!me && (client.manager?.email || '').toLowerCase() === me && (client.partner?.email || '').toLowerCase() === me;
  };

  // `extra` rides along on the body — { pullToSelf: true } makes the server write the manager sign-off AND
  // the partner's in one transaction (nothing half-saved if it fails).
  const postSignoff = (client, role, extra = {}) => trackMutation(() => postJson(
    `/api/diSection/${period}/client-billing-review/${role}/${encodeURIComponent(email)}`,
    { entries: [{ recordKey: String(client.contIndex), fieldKey: '_signoff', value: null, isSignoff: true }], ...extra },
  ));

  // Partner claims a client whose manager hasn't reviewed yet — writes a
  // manager signoff first (attributed to partner's email but role='manager' so
  // the pull is visible in audit), then the partner signoff. Full revert on
  // any failure since the whole point of "pull" is to get both done.
  async function pullClientToSelf(client) {
    const snapshotReviews = client.reviews;
    const stamp = { reviewedBy: 'You', reviewedAt: new Date().toISOString() };

    patchClient(client.contIndex, c => ({
      ...c,
      reviews: { ...c.reviews, manager: stamp, partner: stamp },
    }));
    setSavingReview(prev => new Set(prev).add(client.contIndex));

    try {
      // ONE request: the server signs the manager role too, first (a both-roles user, or the pull-to-self
      // cascade), in a single transaction.
      await postSignoff(client, 'partner', holdsBothRoles(client) ? {} : { pullToSelf: true });
      toast.success(`✓ Pulled ${client.clientCode} to partner (manager + partner both marked).`);
    } catch (err) {
      // Atomic on the server: a failure saved nothing, so put everything back.
      patchClient(client.contIndex, c => ({ ...c, reviews: snapshotReviews }));
      toast.error(`Pull failed: ${err.message}`, { autoClose: 10000 });
    } finally {
      setSavingReview(prev => { const n = new Set(prev); n.delete(client.contIndex); return n; });
    }
  }

  async function markClientReviewed(client) {
    const isSaving = savingReview.has(client.contIndex);
    if (isSaving || !periodActive) return;

    // Partner reviewing a client whose manager hasn't signed off → pull-to-self.
    if (activeRole === 'partner' && !client.reviews?.manager) {
      return pullClientToSelf(client);
    }

    // Cascade precheck based on current local state — covers the case where a
    // single user is BOTH manager and partner on this client.
    const otherRole  = activeRole === 'manager' ? 'partner' : 'manager';
    const isBothRoles = holdsBothRoles(client);
    const otherAlreadyReviewed = !!client.reviews?.[otherRole];
    const cascaded = isBothRoles && !otherAlreadyReviewed;

    const snapshotReviews = client.reviews;
    const stamp = { reviewedBy: 'You', reviewedAt: new Date().toISOString() };

    // Optimistic: mark active role (and cascade's other role if applicable).
    patchClient(client.contIndex, c => ({
      ...c,
      reviews: {
        ...c.reviews,
        [activeRole]: stamp,
        ...(cascaded ? { [otherRole]: stamp } : {}),
      },
    }));
    setSavingReview(prev => new Set(prev).add(client.contIndex));

    try {
      await postSignoff(client, activeRole);
    } catch (err) {
      // Primary failed — revert everything.
      patchClient(client.contIndex, c => ({ ...c, reviews: snapshotReviews }));
      toast.error(`Failed to mark reviewed: ${err.message}`, { autoClose: 10000 });
      setSavingReview(prev => { const n = new Set(prev); n.delete(client.contIndex); return n; });
      return;
    }

    // The server wrote the other role's sign-off with it when one person holds both roles.
    toast.success(cascaded
      ? `✓ Marked ${client.clientCode} reviewed (manager + partner).`
      : `✓ Marked ${client.clientCode} as reviewed.`);
    setSavingReview(prev => { const n = new Set(prev); n.delete(client.contIndex); return n; });
  }

  // ── bulk approval ────────────────────────────────────────────────────────
  // "Bulk Approval…" enters a selection mode (same shape as Client Data's bulk edit): tick clients, or
  // "Select all N matching filters", then sign off the selection. Signing is the same as the row toggle,
  // including pull-to-self (a partner whose manager hasn't reviewed writes both) and the manager+partner
  // cascade when you hold both roles. Only clients the active role hasn't reviewed yet can be ticked, and
  // never one with a billing-group violation: that is a data problem the partner has to fix first (they
  // can still be reviewed one at a time).
  const bulkCandidates = periodActive && activeRole ? filtered.filter(c => !c.reviews?.[activeRole]) : [];
  const bulkSkipped = bulkCandidates.filter(c => c.billingGroup?.violation);
  const selectableRows = bulkCandidates.filter(c => !c.billingGroup?.violation);
  // Selection is limited to what's currently shown, so a filter change can't leave hidden clients
  // silently selected.
  const bulkTargets = bulkMode ? selectableRows.filter(c => selected.has(c.contIndex)) : [];
  const pageSelectable = paged.filter(c => selectableRows.includes(c));
  const pageAllSelected = pageSelectable.length > 0 && pageSelectable.every(c => selected.has(c.contIndex));
  const toggleOne = c => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(c.contIndex)) n.delete(c.contIndex); else n.add(c.contIndex);
    return n;
  });
  const togglePage = () => setSelected(prev => {
    const n = new Set(prev);
    pageSelectable.forEach(c => { if (pageAllSelected) n.delete(c.contIndex); else n.add(c.contIndex); });
    return n;
  });
  const selectAllMatching = () => setSelected(new Set(selectableRows.map(c => c.contIndex)));
  function exitBulk() { setBulkMode(false); setSelected(new Set()); }
  const codeList = (codes, max = 8) => `${codes.slice(0, max).join(', ')}${codes.length > max ? `, +${codes.length - max} more` : ''}`;

  // Roles that will show as signed once this client is done (the optimistic stamp).
  function rolesToSign(client) {
    if (activeRole === 'partner' && !client.reviews?.manager) return ['manager', 'partner'];
    const otherRole = activeRole === 'manager' ? 'partner' : 'manager';
    return holdsBothRoles(client) && !client.reviews?.[otherRole] ? [activeRole, otherRole] : [activeRole];
  }
  // Roles actually POSTed: one request is enough — a both-roles user's other role, and a partner's
  // pull-to-self manager sign-off, are written by the server in the same transaction.
  const rolesToPost = () => [activeRole];
  const pullFlag = client => (activeRole === 'partner' && !client.reviews?.manager && !holdsBothRoles(client) ? { pullToSelf: true } : {});

  async function bulkApprove(confirmed = false) {
    if (bulkBusy || !bulkTargets.length) return;
    const targets = bulkTargets;
    if (!confirmed) {
      // A partner signing for clients the manager hasn't reviewed gets the warning modal (Continue
      // re-enters here confirmed); otherwise the plain confirm.
      const unsigned = activeRole === 'partner' ? targets.filter(c => !c.reviews?.manager && !holdsBothRoles(c)).length : 0;
      if (unsigned) { setMgrWarn({ unsigned, total: targets.length }); return; }
      const msg = `Mark ${targets.length} selected client${targets.length === 1 ? '' : 's'} as reviewed (${activeRole})?\n\nReviewed clients lock their edits.`;
      if (!window.confirm(msg)) return;
    }
    setMgrWarn(null);
    exitBulk();

    const stamp = { reviewedBy: 'You', reviewedAt: new Date().toISOString() };
    const priors = new Map(targets.map(c => [c.contIndex, c.reviews]));
    const plans = new Map(targets.map(c => [c.contIndex, rolesToSign(c)]));
    setBulkBusy(true);
    setSavingReview(prev => { const n = new Set(prev); targets.forEach(c => n.add(c.contIndex)); return n; });
    // Optimistic: every target shows as reviewed straight away.
    const stampedAll = new Map(targets.map(c => [c.contIndex, Object.fromEntries(plans.get(c.contIndex).map(r => [r, stamp]))]));
    setData(prev => prev && ({
      ...prev,
      clients: prev.clients.map(c => stampedAll.has(c.contIndex) ? { ...c, reviews: { ...c.reviews, ...stampedAll.get(c.contIndex) } } : c),
    }));

    let ok = 0;
    const failed = [];
    await runLimited(targets, 5, async client => {
      const done = [];
      try {
        for (const role of rolesToPost(client)) { await postSignoff(client, role, pullFlag(client)); done.push(role); }
        done.push(...plans.get(client.contIndex).filter(r => !done.includes(r)));   // roles the server signed with it
        ok++;
      } catch (err) {
        failed.push(client.clientCode);
        // Keep whatever signed off; put back the rest.
        patchClient(client.contIndex, c => ({
          ...c,
          reviews: { ...priors.get(client.contIndex), ...Object.fromEntries(done.map(r => [r, stamp])) },
        }));
      } finally {
        setSavingReview(prev => { const n = new Set(prev); n.delete(client.contIndex); return n; });
      }
    });
    setBulkBusy(false);
    if (failed.length) {
      toast.error(`${failed.length} of ${targets.length} couldn't be marked reviewed (${failed.slice(0, 5).join(', ')}${failed.length > 5 ? '…' : ''}).`, { autoClose: 10000 });
    }
    if (ok) toast.success(`✓ Marked ${ok} client${ok === 1 ? '' : 's'} as reviewed.`);
  }

  function openReport(codes, title) {
    setReportCodes(codes);
    setReportTitle(title || '');
    setReportOpen(true);
  }

  // ── expanded detail panel ────────────────────────────────────────────────

  function renderDetail(client) {
    const reviewed = client.reviews?.[activeRole];
    // Edits freeze once the active role has reviewed. Period-closed also freezes.
    const frozen = !periodActive || !!reviewed;
    // Once this role has signed off, submissions are final — the Billing Team approves or
    // denies what's queued, so no further edits and no withdrawing (the server enforces it).
    const withdrawIfOpen = frozen ? undefined : withdraw;

    return (
      <div className="br-detail-wrapper">
        <div className="br-detail-toolbar" onClick={e => e.stopPropagation()}>
          <button
            type="button"
            className="br-report-btn"
            onClick={() => openReport([client.clientCode], `${client.clientCode} — ${client.clientName}`)}
            title="Open the Client Billing Report for this client"
          >
            <i className="fas fa-chart-line" /> Client Billing Report
          </button>
        </div>
        <div className="br-detail">
        {/* Billing Group */}
        <div className="detail-section">
          <h4>Billing Group</h4>
          <BillingGroupEditor
            client={client}
            period={period}
            readOnly={frozen}
            searchParents={needle => searchParentCandidates(period, needle, client.clientCode)}
            fetchGroup={code => fetchGroupMembers(period, code, client.clientCode)}
            onOptimisticUpdate={updater => patchClient(client.contIndex, updater)}
            onChanged={silentRefetch}
            pending={pendingFor(client, 'billing_group', 'billing_group')}
            onWithdraw={withdrawIfOpen}
          />
        </div>

        {/* Client Standards */}
        <div className="detail-section">
          <h4>Client Standards</h4>
          <ClientStandardsEditor
            client={client}
            period={period}
            readOnly={frozen}
            availableServices={data.availableServices || []}
            onOptimisticUpdate={updater => patchClient(client.contIndex, updater)}
            onChanged={silentRefetch}
            onWithdraw={withdrawIfOpen}
          />
        </div>

        {/* Narrative Standards — staged like every other edit */}
        <div className="detail-section">
          <h4>Narrative Standards</h4>
          <NarrativeStandardsSummary
            client={client}
            period={period}
            reloadToken={narrReload}
            readOnly={frozen}
            onConfigure={() => setNarrContIndex(client.contIndex)}
          />
        </div>

        {/* Force Bill (+ GCC threshold) */}
        <div className="detail-section">
          <h4>Force Bill</h4>
          <ForceBillEditor
            client={client}
            period={period}
            readOnly={frozen}
            availableServices={data.availableServices || []}
            onOptimisticUpdate={updater => patchClient(client.contIndex, updater)}
            onChanged={silentRefetch}
            onWithdraw={withdrawIfOpen}
          />
        </div>

        {/* Exclude Client */}
        <div className="detail-section">
          <h4>Exclude Client</h4>
          <ConfigToggle
            label="Exclude from automated billing"
            description="When enabled, this client is skipped entirely by the automated billing run — no drafts are generated regardless of WIP or any other configuration."
            checked={client.excludeClient}
            disabled={frozen || !activeRole}
            saving={savingKeys.has(`${client.contIndex}:excludeClient`)}
            pending={pendingFor(client, 'misc', 'excludeClient')}
            onWithdraw={withdrawIfOpen}
            onChange={v => setFlag(client, 'excludeClient', 'Exclude from automated billing', v)}
          />
        </div>

        {/* Recurring */}
        <div className="detail-section">
          <h4>Recurring Billing</h4>
          <RecurringBillingEditor
            client={client}
            period={period}
            readOnly={frozen}
            availableServices={data.availableServices || []}
            jobMapping={data.jobMapping || []}
            onOptimisticUpdate={updater => patchClient(client.contIndex, updater)}
            onChanged={silentRefetch}
            onWithdraw={withdrawIfOpen}
          />
        </div>

        {/* Misc Configurations */}
        <div className="detail-section detail-section--wide">
          <h4>MISC CONFIGURATIONS</h4>

          <ConfigToggle
            label="Tech Fee Narrative Line Broken Out"
            description="When enabled, the tech fee appears as its own broken-out line item on this client's invoice instead of being rolled into the main narrative."
            checked={client.techFee}
            disabled={frozen || !activeRole}
            saving={savingKeys.has(`${client.contIndex}:techFee`)}
            pending={pendingFor(client, 'misc', 'techFee')}
            onWithdraw={withdrawIfOpen}
            onChange={v => setFlag(client, 'techFee', 'Tech fee', v)}
          />
          <ConfigToggle
            label="Append Client Name on Invoice"
            description="When enabled, this client's name is appended to the invoice narrative so it's visible when the client is billed as part of a billing group with multiple children."
            checked={client.billingGroup?.appendClientName}
            disabled={frozen || !activeRole}
            saving={savingKeys.has(`${client.contIndex}:append_client_name`)}
            pending={pendingFor(client, 'billing_group', 'append_client_name')}
            onWithdraw={withdrawIfOpen}
            onChange={v => setAppendClientName(client, v)}
          />
          <ConfigToggle
            label="Freebie Return"
            description="When enabled, this client will receive a $1,500 credit on their returns."
            checked={client.freebieReturn}
            disabled={frozen || !activeRole}
            saving={savingKeys.has(`${client.contIndex}:freebieReturn`)}
            pending={pendingFor(client, 'misc', 'freebieReturn')}
            onWithdraw={withdrawIfOpen}
            onChange={v => requestExclusiveFlag(client, 'freebieReturn', v)}
          />
          <ConfigToggle
            label="Roll INDTAX WIP into highest BUSTAX narrative line"
            description="When enabled, the automated billing run rolls this client's INDTAX WIP into the highest-amount BUSTAX narrative line instead of billing it separately."
            checked={client.rollIndtaxIntoBustax}
            disabled={frozen || !activeRole}
            saving={savingKeys.has(`${client.contIndex}:rollIndtaxIntoBustax`)}
            pending={pendingFor(client, 'misc', 'rollIndtaxIntoBustax')}
            onWithdraw={withdrawIfOpen}
            onChange={v => setFlag(client, 'rollIndtaxIntoBustax', 'Roll INDTAX into BUSTAX', v)}
          />
          <ConfigToggle
            label="Discount Toggle"
            description="When enabled, this client's narratives are shown at 100% of WIP with a separate negative line displaying their discount. Does NOT change their realization calculation."
            checked={client.discountToggle}
            disabled={frozen || !activeRole}
            saving={savingKeys.has(`${client.contIndex}:discountToggle`)}
            pending={pendingFor(client, 'misc', 'discountToggle')}
            onWithdraw={withdrawIfOpen}
            onChange={v => requestExclusiveFlag(client, 'discountToggle', v)}
          />

          {/* Delivery formats — written to Practice Engine once the Billing Team approves. */}
          <div className="br-delivery">
            {[['ClientInvDeliveryFormat', 'Invoice Delivery Format'], ['ClientDeliveryFormat', 'Statement Delivery Format']].map(([field, label]) => {
              const raw = client[field];
              const val = raw === 0 || raw === 1 ? String(raw) : '';
              return (
                <div className="br-delivery-col" key={field}>
                  <label className="br-delivery-label">{label}</label>
                  <select
                    className="br-sel"
                    value={val}
                    disabled={frozen || !activeRole || data.deliveryUnavailable || savingKeys.has(`${client.contIndex}:${field}`)}
                    onChange={e => requestDeliveryFormat(client, field, Number(e.target.value))}
                  >
                    {val === '' && <option value="" disabled>Select…</option>}
                    <option value="1">Email</option>
                    <option value="0">Print and Mail</option>
                  </select>
                  <PendingTag change={pendingFor(client, 'delivery_format', field)} onWithdraw={withdrawIfOpen} />
                </div>
              );
            })}
          </div>
          <p className="br-pe-note">
            {data.deliveryUnavailable
              ? "Current delivery formats couldn't be loaded from Practice Engine right now, so they can't be edited. Refresh in a minute."
              : 'Delivery format changes are written to Practice Engine when approved.'}
          </p>
        </div>
      </div>
      </div>
    );
  }

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <div className="billing-review">
        {/* Header — Dashboard sits inline at the left (no separate row), Report
            + role toggle at the right. */}
        <div className="br-header">
          <div className="br-title-block">
            {backButton}
            <h2 className="br-title">Client Billing Data Review</h2>
            {!periodActive && <span className="br-readonly-badge">Read-only</span>}
            <LastRefreshed at={lastRefreshedAt} freshness={freshness} onRefresh={silentRefetch} />
          </div>
          <div className="br-header-actions">
            <RoleToggle role={activeRole} onChange={changeRole} disabled={initialLoading} withApprovals={withApprovals} />
          </div>
        </div>

        <div className="br-approval-note">
          <i className="fas fa-info-circle" />
          <span>
            The Billing Team will review and approve all updates made on this page before they are imported into the Botfield Billing App.
          </span>
        </div>

        {/* Progress Bars */}
        {(() => {
          // Accurate counts computed from what's actually loaded on the page for
          // the active role — independent of the audit-log-touched denominator.
          const reviewedNow = clients.filter(c => c.reviews?.[activeRole]).length;
          const totalNow    = clients.length;
          const accuratePct = totalNow > 0 ? Math.round((reviewedNow / totalNow) * 100) : 0;
          return (
          <div className="br-progress-section">
            <div className="progress-header">
              <h3 className="progress-title">Your Progress</h3>
              <span className="overall-pct">
                <span className="overall-count">{reviewedNow} / {totalNow}</span>
                <span className="overall-divider">—</span>
                {accuratePct}% Complete
              </span>
            </div>
            <div className="progress-bar-container">
              <div className="progress-track">
                <div
                  className="progress-fill"
                  style={{
                    width: `${Math.min(100, Math.max(0, accuratePct))}%`,
                    background: accuratePct >= 100 ? '#1a7a4a' : accuratePct >= 50 ? '#e6a817' : '#c0392b',
                  }}
                />
              </div>
            </div>
          </div>
          );
        })()}

        {/* Filters — search + a Filters popover (Partner / Manager / Office + "only
            show clients with…" toggles) with removable chips for what's active. */}
        <div className="br-filters">
          <div className="filter-group">
            <i className="fas fa-search filter-icon" />
            <input
              type="text"
              placeholder="Search by code or name..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="filter-input"
            />
          </div>

          <div className="br-fp-wrap" ref={filterPanelRef}>
            <button
              type="button"
              className={`br-fp-trigger ${activeChips.length ? 'br-fp-trigger--active' : ''}`}
              onClick={() => setShowFilterPanel(v => !v)}
              title="Filters"
            >
              <i className="fas fa-filter" /> Filters
              {activeChips.length > 0 && <span className="br-fp-badge">{activeChips.length}</span>}
            </button>

            {showFilterPanel && (
              <div className="br-fp-panel" role="dialog" aria-label="Filter panel">
                <div className="br-fp-panel__header">
                  <span className="br-fp-panel__title">Filters</span>
                  <div className="br-fp-panel__header-actions">
                    <button type="button" className="br-fp-panel__clear" onClick={clearFilters} disabled={!anyFilter}>
                      Clear All
                    </button>
                    <button type="button" className="br-fp-panel__close" onClick={() => setShowFilterPanel(false)} aria-label="Close filter panel">&times;</button>
                  </div>
                </div>
                <div className="br-fp-panel__body">
                  <div className="br-fp-field">
                    <label className="br-fp-label">Partner</label>
                    <select className="br-fp-select" value={partnerFilter} onChange={e => { setPartnerFilter(e.target.value); setPage(1); }}>
                      <option value="">All</option>
                      {partners.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div className="br-fp-field">
                    <label className="br-fp-label">Manager</label>
                    <select className="br-fp-select" value={managerFilter} onChange={e => { setManagerFilter(e.target.value); setPage(1); }}>
                      <option value="">All</option>
                      {managers.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div className="br-fp-field">
                    <label className="br-fp-label">Office</label>
                    <select className="br-fp-select" value={officeFilter} onChange={e => { setOfficeFilter(e.target.value); setPage(1); }}>
                      <option value="">All</option>
                      {offices.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>

                  <div className="br-fp-divider" />

                  <div className="br-fp-toggles">
                    <div className="br-fp-toggles__heading">Only show clients with&hellip;</div>
                    {BOOL_FILTERS.map(f => (
                      <label key={f.key} className={`br-fp-toggle ${!boolFilters[f.key] && !boolCounts[f.key] ? 'br-fp-toggle--none' : ''}`}>
                        <input
                          type="checkbox"
                          checked={!!boolFilters[f.key]}
                          disabled={!boolFilters[f.key] && !boolCounts[f.key]}
                          onChange={e => { const next = e.target.checked; setBoolFilters(prev => ({ ...prev, [f.key]: next })); setPage(1); }}
                        />
                        <span>{f.label}</span>
                        <span className="br-fp-toggle__count">{boolCounts[f.key]}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {activeRole === 'partner' && (
            <>
              <button
                type="button"
                className={`br-quick-filter ${ptrMgrSigned ? 'br-quick-filter--on' : ''}`}
                aria-pressed={ptrMgrSigned}
                onClick={() => { setPtrMgrSigned(v => !v); setPage(1); }}
                title="Only show clients whose manager has signed off"
              >
                <i className={`fas ${ptrMgrSigned ? 'fa-check-square' : 'fa-square'}`} /> Manager signed off
              </button>
              <button
                type="button"
                className={`br-quick-filter ${ptrChangedOnly ? 'br-quick-filter--on' : ''}`}
                aria-pressed={ptrChangedOnly}
                onClick={() => { setPtrChangedOnly(v => !v); setPage(1); }}
                title="Only show clients that have change requests this period"
              >
                <i className={`fas ${ptrChangedOnly ? 'fa-check-square' : 'fa-square'}`} /> Changed clients only
              </button>
            </>
          )}

          <button
            type="button"
            className="br-report-btn br-report-btn--bar"
            onClick={() => openReport(null, 'All of your clients')}
            title="Open the Client Billing Report for your clients"
          >
            <i className="fas fa-chart-line" /> Client Billing Report
          </button>

          {!bulkMode && (
            <button
              type="button"
              className="br-bulk-btn"
              disabled={!periodActive || !activeRole || selectableRows.length === 0 || bulkBusy}
              onClick={() => setBulkMode(true)}
              title={selectableRows.length
                ? 'Pick clients to mark as reviewed in one go'
                : (bulkSkipped.length ? 'Every unreviewed client shown has a billing group violation' : 'Every client shown is already reviewed')}
            >
              <i className={`fas ${bulkBusy ? 'fa-spinner fa-spin' : 'fa-check-double'}`} /> Bulk Approval…
            </button>
          )}
        </div>

        {bulkMode && (
          <div className="br-bulkbar">
            <strong>{bulkTargets.length}</strong> of {selectableRows.length} client{selectableRows.length === 1 ? '' : 's'} selected
            {selectableRows.length > bulkTargets.length && (
              <button type="button" className="br-link" onClick={selectAllMatching}>
                Select all {selectableRows.length.toLocaleString()}{anyFilter ? ' matching filters' : ''}
              </button>
            )}
            {bulkTargets.length > 0 && <button type="button" className="br-link" onClick={() => setSelected(new Set())}>Clear selection</button>}
            {activeRole === 'partner' && (
              <button
                type="button"
                className={`br-quick-filter ${ptrMgrSigned ? 'br-quick-filter--on' : ''}`}
                aria-pressed={ptrMgrSigned}
                onClick={() => { setPtrMgrSigned(v => !v); setPage(1); }}
                title="Only show clients whose manager has signed off (same filter as the one beside Filters)"
              >
                <i className={`fas ${ptrMgrSigned ? 'fa-check-square' : 'fa-square'}`} /> Manager signed off
              </button>
            )}
            {bulkSkipped.length > 0 && (
              <span className="br-bulkbar-note" title={codeList(bulkSkipped.map(c => c.clientCode), 12)}>
                {bulkSkipped.length} with a billing group violation can't be bulk-signed
              </span>
            )}
            <span className="br-bulkbar-spacer" />
            <button
              type="button"
              className="br-bulk-btn br-bulk-btn--bar"
              disabled={bulkTargets.length === 0 || bulkBusy}
              onClick={() => bulkApprove()}
              title="Mark the selected clients as reviewed (a partner sign-off also signs the Manager's when they haven't yet)"
            >
              <i className="fas fa-check-double" /> Mark {bulkTargets.length || ''} selected reviewed
            </button>
            <button type="button" className="br-link br-link--plain" onClick={exitBulk}>Cancel</button>
          </div>
        )}

        {activeChips.length > 0 && (
          <div className="br-active-chips">
            {activeChips.map(chip => (
              <span key={chip.key} className="br-chip">
                {chip.label}
                <button type="button" className="br-chip__clear" onClick={() => { chip.clear(); setPage(1); }} aria-label={`Clear ${chip.label}`}>
                  <i className="fas fa-times" />
                </button>
              </span>
            ))}
            <button type="button" className="br-chip br-chip--clear-all" onClick={clearFilters}>Clear all</button>
          </div>
        )}

        {activeRole === 'partner' && clients.some(c => !c.reviews?.manager) && (
          <div className="rt-partner-note">
            <i className="fas fa-info-circle" />
            <span>
              If your manager hasn't reviewed a client yet, you can still mark it reviewed —
              your action will sign off as both Manager and Partner.
            </span>
          </div>
        )}

        {/* Table */}
        <div className="br-table-wrap">
          <table className="br-table">
            <thead>
              <tr>
                {bulkMode && (
                  <th className="col-select">
                    <input type="checkbox" aria-label="Select all on this page"
                           checked={pageAllSelected} disabled={pageSelectable.length === 0} onChange={togglePage} />
                  </th>
                )}
                <th className="col-expand" />
                <th>Code</th>
                <th>Client Name</th>
                <th>Office</th>
                <th>Partner</th>
                <th>Manager</th>
                <th>Billing Group</th>
                <th>Client Std</th>
                <th>Tech Fee</th>
                <th>Append Name</th>
                <th>Recurring Mo.</th>
                <th>Recurring Qtr.</th>
                <th className={`col-review ${activeRole === 'manager' ? 'col-review-wide' : ''}`}>Mgr</th>
                {activeRole === 'partner' && <th className="col-review col-review-wide">Ptr</th>}
              </tr>
            </thead>
            {paged.map(client => {
              const isExpanded = expanded.has(client.contIndex);
              const colCount = (activeRole === 'partner' ? 14 : 13) + (bulkMode ? 1 : 0);
              const isSel = bulkMode && selected.has(client.contIndex);
              const canTick = selectableRows.includes(client);
              return (
                <tbody key={client.contIndex} className="client-group">
                  <tr
                    className={`br-row ${isExpanded ? 'row-expanded' : ''} ${client.billingGroup?.violation ? 'row-violation' : ''} ${isSel ? 'br-row-sel' : ''}`}
                    onClick={() => toggle(client.contIndex)}
                  >
                    {bulkMode && (
                      <td className="cell-select" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" aria-label={`Select ${client.clientCode}`}
                               checked={isSel} disabled={!canTick}
                               title={canTick ? undefined
                                 : (client.billingGroup?.violation && !client.reviews?.[activeRole]
                                   ? 'Billing group violation — fix it first, then review this client on its own'
                                   : 'Already reviewed')}
                               onChange={() => toggleOne(client)} />
                      </td>
                    )}
                    <td className="cell-expand">
                      <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} />
                    </td>
                    <td className="cell-code">{client.clientCode}</td>
                    <td className="cell-name">
                      {client.clientName}
                      {client.pending?.length > 0 && (
                        <i className="fas fa-hourglass-half br-pending-dot" title={`${client.pending.length} change(s) pending Billing Team approval`} />
                      )}
                    </td>
                    <td>{client.clientOffice}</td>
                    <td>{client.partner.name}</td>
                    <td>{client.manager.name}</td>
                    {renderBgCell(client)}
                    {renderStdCell(client.clientStandards)}
                    {renderTechFeeCell(client)}
                    {renderAppendCell(client)}
                    {renderRecurCell(client.recurringMonthly, client.recurringMonthlyDupe)}
                    {renderRecurCell(client.recurringQuarterly, client.recurringQuarterlyDupe)}
                    {activeRole === 'manager'
                      ? renderReviewActionCell(client)
                      : <td className="cell-review">{renderReviewBadge(client.reviews, 'manager')}</td>}
                    {activeRole === 'partner' && renderReviewActionCell(client)}
                  </tr>
                  {isExpanded && (
                    <tr className="br-detail-row">
                      <td colSpan={colCount}>
                        {renderDetail(client)}
                      </td>
                    </tr>
                  )}
                </tbody>
              );
            })}
          </table>
        </div>

        {/* Pagination */}
        {totalFiltered > PAGE_SIZE && (
          <div className="br-pagination">
            <button disabled={safePage <= 1} onClick={() => setPage(1)}><i className="fas fa-angle-double-left" /></button>
            <button disabled={safePage <= 1} onClick={() => setPage(p => p - 1)}><i className="fas fa-angle-left" /></button>
            <span className="page-info">Page {safePage} of {totalPages} <span className="page-total">({totalFiltered} clients)</span></span>
            <button disabled={safePage >= totalPages} onClick={() => setPage(p => p + 1)}><i className="fas fa-angle-right" /></button>
            <button disabled={safePage >= totalPages} onClick={() => setPage(totalPages)}><i className="fas fa-angle-double-right" /></button>
          </div>
        )}

        {totalFiltered === 0 && (
          <div className="br-empty">
            {anyFilter
              ? 'No clients match the current filters.'
              : `No clients found for your ${activeRole} role.`}
          </div>
        )}

        {narrContIndex != null && (() => {
          const nc = allClients.find(c => c.contIndex === narrContIndex);
          if (!nc) return null;
          return (
            <NarrativeStandardsModal
              open
              onClose={() => setNarrContIndex(null)}
              client={nc}
              period={period}
              readOnly={!periodActive || !!nc.reviews?.[activeRole]}
              onChanged={() => { silentRefetch(); setNarrReload(t => t + 1); }}
            />
          );
        })()}

        {emailModal && (() => {
          const ec = allClients.find(c => c.contIndex === emailModal.contIndex);
          if (!ec) return null;
          return (
            <DeliveryEmailModal
              client={ec}
              onCancel={() => setEmailModal(null)}
              onSubmit={emails => { setEmailModal(null); proposeDeliveryFormat(ec, emailModal.field, 1, emails); }}
            />
          );
        })()}

        {mgrWarn && (
          <ManagerUnsignedWarningModal
            unsigned={mgrWarn.unsigned}
            total={mgrWarn.total}
            cancelClass="cs-btn"
            continueClass="cs-btn cs-btn--save"
            onCancel={() => setMgrWarn(null)}
            onContinue={() => bulkApprove(true)}
          />
        )}

        {excludeModal && (() => {
          const ec = allClients.find(c => c.contIndex === excludeModal.contIndex);
          const x = EXCLUSIVE[excludeModal.fieldKey];
          if (!ec) return null;
          const close = () => setExcludeModal(null);
          return (
            <Modal
              open
              onClose={close}
              title={`Turn on ${x.label.toLowerCase()}?`}
              className="gcc-confirm-modal"
              footer={(
                <>
                  <button className="cs-btn" onClick={close}>Cancel</button>
                  <button
                    className="cs-btn cs-btn--save"
                    onClick={() => { close(); applyExclusiveFlag(ec, excludeModal.fieldKey); }}
                  >
                    Continue
                  </button>
                </>
              )}
            >
              <p className="nc-description" style={{ marginLeft: 0 }}>
                <strong>Freebie Return</strong> and <strong>Discount Toggle</strong> can't both be on.
              </p>
              <p className="nc-description" style={{ marginLeft: 0 }}>
                {ec.clientCode} currently has <strong>{x.otherLabel.toLowerCase()}</strong> on. Continuing turns it <strong>off</strong> and
                turns <strong>{x.label.toLowerCase()}</strong> on. Both changes are submitted for Billing Team approval.
              </p>
            </Modal>
          );
        })()}

        {reportOpen && (
          <ClientBillingReportModal
            open
            onClose={() => setReportOpen(false)}
            period={period}
            clientCodes={reportCodes || undefined}
            title={reportTitle}
          />
        )}
    </div>
  );
}
